/* ═══════════════════════════════════════════════════════════════
   BEAUTYONTAPP — iOS GLASS BOTTOM SHEET ENGINE
   Spring physics + touch gestures for shopModal1/2/3
   ~4 KB · Zero dependencies · Vanilla JS
   ═══════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  // ─── SPRING PHYSICS CONFIG (matches Apple's sheet preset) ───
  var SPRING = { stiffness: 300, damping: 30, mass: 1 };
  var REDUCE_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var VELOCITY_THRESHOLD = 0.5;   // px/ms — fast swipe threshold
  var RUBBER_BAND_COEFF = 0.55;   // Apple's rubber-band constant
  var DISMISS_DISTANCE = 0.35;    // 35% of sheet height = dismiss
  var MAX_OVERSCROLL = 40;        // max px the sheet can pull up past open

  // ─── ANIMATION CANCELLATION ───
  // Each sheet tracks its own animation ID. Starting a new animation
  // increments the ID, causing any running animation to stop on its
  // next frame. Prevents double-tap race conditions.
  var sheetAnimIds = {};

  function getAnimId(sheet) {
    var id = sheet.id || 'sheet';
    if (!sheetAnimIds[id]) sheetAnimIds[id] = 0;
    return id;
  }

  function nextAnimId(sheet) {
    var id = getAnimId(sheet);
    sheetAnimIds[id] = sheetAnimIds[id] + 1;
    return sheetAnimIds[id];
  }

  function isAnimCurrent(sheet, animId) {
    var id = getAnimId(sheet);
    return sheetAnimIds[id] === animId;
  }

  // ─── SPRING ANIMATION SOLVER ───
  function springAnimate(sheet, opts) {
    if (REDUCE_MOTION) {
      opts.onUpdate(opts.to);
      if (opts.onComplete) opts.onComplete();
      return;
    }
    var pos = opts.from;
    var vel = opts.velocity || 0;
    var to = opts.to;
    var onUpdate = opts.onUpdate;
    var onComplete = opts.onComplete;
    var lastTime = performance.now();
    var animId = nextAnimId(sheet);

    function step(now) {
      // Stop if a newer animation has started
      if (!isAnimCurrent(sheet, animId)) return;

      var frameDt = Math.min((now - lastTime) / 1000, 0.064);
      lastTime = now;
      // Integrate in fixed <=16ms substeps: semi-implicit Euler with k=300, c=30
      // diverges above dt ~0.052s, so never take one big step on slow frames.
      while (frameDt > 0) {
        var dt = Math.min(frameDt, 0.016);
        frameDt -= dt;
        var displacement = pos - to;
        var accel = (-SPRING.stiffness * displacement - SPRING.damping * vel) / SPRING.mass;
        vel += accel * dt;
        pos += vel * dt;
      }
      onUpdate(pos);

      if (Math.abs(vel) < 0.5 && Math.abs(pos - to) < 0.5) {
        onUpdate(to);
        if (onComplete) onComplete();
      } else {
        requestAnimationFrame(step);
      }
    }
    requestAnimationFrame(step);
  }

  // ─── RUBBER BAND FORMULA ───
  function rubberBand(offset, dimension) {
    return (1.0 - 1.0 / ((offset * RUBBER_BAND_COEFF / dimension) + 1.0)) * dimension;
  }

  // ─── SET SHEET POSITION ───
  // Allows negative values down to -MAX_OVERSCROLL for rubber band effect
  function setSheetY(sheet, y) {
    var clamped = Math.max(-MAX_OVERSCROLL, y);
    sheet.style.transform = 'translateY(' + clamped + 'px)';
  }

  // ─── GET CURRENT Y FROM TRANSFORM ───
  function getCurrentY(sheet) {
    var transform = sheet.style.transform;
    var match = transform.match(/translateY\(([^)]+)px\)/);
    return match ? parseFloat(match[1]) : 0;
  }

  // ─── BACKDROP ───
  var backdrop = null;
  function getBackdrop() {
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = 'sheet-backdrop';
      document.body.appendChild(backdrop);
      backdrop.addEventListener('click', function () {
        closeAllSheets();
      });
    }
    return backdrop;
  }

  function showBackdrop() {
    var bd = getBackdrop();
    bd.classList.add('visible');
  }

  function hideBackdrop() {
    var bd = getBackdrop();
    bd.classList.remove('visible');
  }

  // ─── ACTIVE SHEET TRACKING ───
  var activeSheet = null;
  var pendingOpen = null;
  var returnFocusElement = null;

  function getFocusableElements(sheet) {
    var candidates = sheet.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(candidates, function (element) {
      return element.getClientRects().length > 0 && element.getAttribute('aria-hidden') !== 'true';
    });
  }

  function focusSheet(sheet) {
    var closeButton = sheet.querySelector('.close-btn');
    var focusTarget = closeButton || getFocusableElements(sheet)[0] || sheet;
    try {
      focusTarget.focus({ preventScroll: true });
    } catch (error) {
      focusTarget.focus();
    }
  }

  function restoreFocus() {
    var focusTarget = returnFocusElement;
    returnFocusElement = null;
    if (!focusTarget || !focusTarget.isConnected) return;
    try {
      focusTarget.focus({ preventScroll: true });
    } catch (error) {
      focusTarget.focus();
    }
  }

  function setTriggerExpanded(sheet, expanded) {
    if (!sheet || !sheet.id) return;
    var trigger = document.querySelector('[aria-controls="' + sheet.id + '"]');
    if (trigger) trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  function cancelPendingOpen() {
    pendingOpen = null;
  }

  function closeAllSheets() {
    cancelPendingOpen();
    var sheets = document.querySelectorAll('.shop-modal.glass-sheet.open');
    for (var i = 0; i < sheets.length; i++) {
      animateClose(sheets[i]);
    }
  }

  // ─── INJECT DRAG HANDLE INTO A MODAL ───
  function injectDragHandle(modal) {
    if (modal.querySelector('.sheet-drag-handle')) return;
    var handle = document.createElement('div');
    handle.className = 'sheet-drag-handle';
    handle.innerHTML = '<div class="sheet-drag-pill"></div>';
    modal.insertBefore(handle, modal.firstChild);
    return handle;
  }

  function bindCloseButtons(scope) {
    var closeBtns = scope.querySelectorAll('.shop-modal .close-btn, .close-btn');
    closeBtns.forEach(function (btn) {
      if (btn.__beautySheetReady) return;
      btn.__beautySheetReady = true;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        cancelPendingOpen();
        var modal = btn.closest('.shop-modal');
        if (modal) animateClose(modal);
      });
    });
  }

  function hydrateSheet(sheet) {
    if (!sheet || sheet.dataset.botSheetHydrated === 'true') return;
    var template = sheet.querySelector('template[data-bot-sheet-template]');
    if (!template) {
      sheet.dataset.botSheetHydrated = 'true';
      bindCloseButtons(sheet);
      return;
    }

    sheet.appendChild(template.content.cloneNode(true));
    template.remove();
    sheet.dataset.botSheetHydrated = 'true';
    bindCloseButtons(sheet);
    document.dispatchEvent(new CustomEvent('beauty:sheet-hydrated', {
      detail: { id: sheet.id }
    }));
  }

  function hydrateSheetAfterFirstPaint(sheet) {
    if (!sheet || sheet.dataset.botSheetHydrationPending === 'true') return;
    if (!sheet.querySelector('template[data-bot-sheet-template]')) return;
    sheet.dataset.botSheetHydrationPending = 'true';
    window.requestAnimationFrame(function () {
      window.setTimeout(function () {
        hydrateSheet(sheet);
        delete sheet.dataset.botSheetHydrationPending;
      }, 0);
    });
  }

  // ─── ANIMATE OPEN ───
  function animateOpen(sheet, preserveReturnFocus, trigger) {
    if (!sheet || sheet.classList.contains('sheet-open')) return;
    if (!preserveReturnFocus) {
      returnFocusElement = trigger || (document.activeElement && document.activeElement !== document.body
        ? document.activeElement
        : null);
    }
    var sheetHeight = window.innerHeight || 800;
    sheet.__beautyMeasuredHeight = sheetHeight;
    setSheetY(sheet, sheetHeight);

    activeSheet = sheet;
    sheet.setAttribute('aria-hidden', 'false');
    setTriggerExpanded(sheet, true);
    sheet.classList.add('sheet-animating');
    sheet.classList.remove('closing');
    sheet.classList.add('open');

    showBackdrop();
    document.body.classList.add('sheet-scroll-lock');

    requestAnimationFrame(function () {
      springAnimate(sheet, {
        from: sheetHeight,
        to: 0,
        velocity: 0,
        onUpdate: function (y) { setSheetY(sheet, y); },
        onComplete: function () {
          sheet.classList.add('sheet-open');
          sheet.classList.remove('sheet-animating');
          sheet.style.willChange = 'auto';
          focusSheet(sheet);
        }
      });
    });
  }

  // ─── ANIMATE CLOSE ───
  function animateClose(sheet, velocity) {
    if (!sheet || !sheet.classList.contains('open')) return;
    sheet.classList.add('sheet-animating');
    sheet.style.willChange = 'transform';
    var sheetHeight = sheet.__beautyMeasuredHeight || window.innerHeight || 800;

    springAnimate(sheet, {
      from: getCurrentY(sheet),
      to: sheetHeight + 20,
      velocity: velocity || 0,
      onUpdate: function (y) { setSheetY(sheet, y); },
      onComplete: function () {
        sheet.classList.remove('sheet-open', 'open', 'sheet-animating');
        sheet.setAttribute('aria-hidden', 'true');
        setTriggerExpanded(sheet, false);
        sheet.style.transform = '';
        sheet.style.willChange = 'auto';
        // Only the sheet that is still active may clear global state: a sheet
        // finishing its close AFTER a replacement opened must not hide the
        // backdrop or null the new activeSheet (sheet-switch race).
        var nextSheet = pendingOpen;
        pendingOpen = null;
        if (activeSheet === sheet) {
          activeSheet = null;
          if (!nextSheet) {
            hideBackdrop();
            restoreFocus();
          }
        }
        if (!nextSheet && !document.querySelector('.shop-modal.open')) {
          document.body.classList.remove('sheet-scroll-lock');
        }
        document.dispatchEvent(new CustomEvent('beauty:sheet-closed', {
          detail: { id: sheet.id || '' }
        }));
        if (nextSheet && nextSheet.isConnected) {
          animateOpen(nextSheet, true);
        }
      }
    });
  }

  // ─── TOUCH GESTURE HANDLER FOR DRAG HANDLE ───
  function initGesture(sheet) {
    if (sheet.__beautyGestureReady) return;
    var handle = sheet.querySelector('.sheet-drag-handle');
    if (!handle) return;
    sheet.__beautyGestureReady = true;

    var startY = 0, lastY = 0, lastTime = 0, velocityY = 0;
    var sheetHeight = 0, isDragging = false;

    handle.addEventListener('pointerdown', function (e) {
      if (!sheet.classList.contains('sheet-open')) return;
      e.preventDefault();
      handle.setPointerCapture(e.pointerId);
      isDragging = true;
      startY = e.clientY;
      lastY = e.clientY;
      lastTime = performance.now();
      velocityY = 0;
      sheetHeight = sheet.__beautyMeasuredHeight || window.innerHeight || 800;
      // Cancel any running animation
      nextAnimId(sheet);
      sheet.style.transition = 'none';
      sheet.style.willChange = 'transform';
      sheet.classList.add('sheet-animating');
    });

    handle.addEventListener('pointermove', function (e) {
      if (!isDragging) return;
      var now = performance.now();
      var dt = now - lastTime;
      if (dt > 0) velocityY = (e.clientY - lastY) / dt;
      lastY = e.clientY;
      lastTime = now;
      var deltaY = e.clientY - startY;
      if (deltaY < 0) deltaY = -rubberBand(-deltaY, sheetHeight);
      setSheetY(sheet, deltaY);
    });

    handle.addEventListener('pointerup', function (e) {
      if (!isDragging) return;
      isDragging = false;
      handle.releasePointerCapture(e.pointerId);
      sheet.style.willChange = 'transform';
      // Stillness guard: pointermove only fires on movement, so after a
      // drag-then-pause the last velocity sample is stale. Zero it so the
      // 35% distance rule decides; real flicks (<16ms-old sample) unaffected.
      if (performance.now() - lastTime > 100) velocityY = 0;

      var currentY = getCurrentY(sheet);
      var shouldDismiss = false;

      if (velocityY > VELOCITY_THRESHOLD) {
        shouldDismiss = true;
      } else if (currentY > sheetHeight * DISMISS_DISTANCE) {
        shouldDismiss = true;
      } else if (velocityY < -VELOCITY_THRESHOLD) {
        shouldDismiss = false;
      }

      if (shouldDismiss) {
        animateClose(sheet, velocityY * 1000);
      } else {
        springAnimate(sheet, {
          from: currentY, to: 0, velocity: velocityY * 1000,
          onUpdate: function (y) { setSheetY(sheet, y); },
          onComplete: function () {
            sheet.classList.remove('sheet-animating');
            sheet.style.willChange = 'auto';
          }
        });
      }
    });

    handle.addEventListener('pointercancel', function () {
      if (!isDragging) return;
      isDragging = false;
      springAnimate(sheet, {
        from: getCurrentY(sheet), to: 0, velocity: 0,
        onUpdate: function (y) { setSheetY(sheet, y); },
        onComplete: function () {
          sheet.classList.remove('sheet-animating');
          sheet.style.willChange = 'auto';
        }
      });
    });
  }

  // ─── SWIPE-TO-DISMISS ON SHEET BODY ───
  function initBodySwipe(sheet) {
    if (sheet.__beautyBodySwipeReady) return;
    sheet.__beautyBodySwipeReady = true;
    var startY = 0, lastY = 0, lastTime = 0, velocityY = 0;
    var isDragging = false, sheetHeight = 0, bodySwipeBlocked = false, bodySwipeReady = false;

    sheet.addEventListener('touchstart', function (e) {
      bodySwipeReady = false;
      if (!sheet.classList.contains('sheet-open')) return;
      var touchTarget = e.target;
      bodySwipeBlocked = !!(touchTarget && touchTarget.closest && touchTarget.closest('.dropdown-menu, .dropdown-submenu'));
      if (bodySwipeBlocked) return;
      if (sheet.scrollTop > 5) return;
      bodySwipeReady = true;
      startY = e.touches[0].clientY;
      lastY = startY;
      lastTime = performance.now();
      velocityY = 0;
      sheetHeight = sheet.__beautyMeasuredHeight || window.innerHeight || 800;
      isDragging = false;
    }, { passive: true });

    sheet.addEventListener('touchmove', function (e) {
      if (bodySwipeBlocked || !bodySwipeReady) return;
      if (!sheet.classList.contains('sheet-open')) return;
      if (sheet.scrollTop > 5) return;
      var currentTouch = e.touches[0].clientY;
      var deltaY = currentTouch - startY;
      if (deltaY < 10 && !isDragging) return;
      isDragging = true;
      // Cancel any running animation on first drag frame
      if (deltaY >= 10) nextAnimId(sheet);
      var now = performance.now();
      var dt = now - lastTime;
      if (dt > 0) velocityY = (currentTouch - lastY) / dt;
      lastY = currentTouch;
      lastTime = now;
      e.preventDefault();
      setSheetY(sheet, deltaY);
    }, { passive: false });

    sheet.addEventListener('touchend', function () {
      if (bodySwipeBlocked) {
        bodySwipeBlocked = false;
        bodySwipeReady = false;
        return;
      }
      if (!bodySwipeReady) return;
      bodySwipeReady = false;
      if (!isDragging) return;
      isDragging = false;
      // Stillness guard (same as drag handle): stale velocity after a pause
      if (performance.now() - lastTime > 100) velocityY = 0;
      var currentY = getCurrentY(sheet);
      var shouldDismiss = velocityY > VELOCITY_THRESHOLD || currentY > sheetHeight * DISMISS_DISTANCE;

      if (shouldDismiss) {
        animateClose(sheet, velocityY * 1000);
      } else {
        springAnimate(sheet, {
          from: currentY, to: 0, velocity: velocityY * 1000,
          onUpdate: function (y) { setSheetY(sheet, y); },
          onComplete: function () {
            sheet.classList.remove('sheet-animating');
            sheet.style.willChange = 'auto';
          }
        });
      }
    }, { passive: true });

    sheet.addEventListener('touchcancel', function () {
      if (bodySwipeBlocked) {
        bodySwipeBlocked = false;
        bodySwipeReady = false;
        return;
      }
      if (!bodySwipeReady) return;
      bodySwipeReady = false;
      if (!isDragging) return;
      isDragging = false;
      springAnimate(sheet, {
        from: getCurrentY(sheet), to: 0, velocity: 0,
        onUpdate: function (y) { setSheetY(sheet, y); },
        onComplete: function () {
          sheet.classList.remove('sheet-animating');
          sheet.style.willChange = 'auto';
        }
      });
    }, { passive: true });
  }

  // ─── KEYBOARD MODAL BEHAVIOUR ───
  document.addEventListener('keydown', function (event) {
    if (!activeSheet || !activeSheet.classList.contains('open')) return;

    var keyTarget = event.target;
    if (keyTarget && keyTarget.closest && keyTarget.closest('[class*="askTimmy"]')) return;

    // Magnific's store chooser is a separate, higher modal. Let it own Escape
    // and Tab while it is present instead of closing the sheet underneath it.
    if (document.querySelector('.mfp-wrap')) return;

    if (event.key === 'Escape') {
      var openDetails = activeSheet.querySelector('.bot-nested-menu > details[open]');
      if (openDetails) {
        event.preventDefault();
        event.stopPropagation();
        var openDropdown = openDetails.closest('.bot-nested-menu');
        var dropdownTrigger = openDetails.querySelector('summary');
        var hideResult = null;
        if (openDropdown && typeof openDropdown.hide === 'function') {
          hideResult = openDropdown.hide();
        } else {
          openDetails.open = false;
        }
        Promise.resolve(hideResult).then(function () {
          if (!dropdownTrigger || !dropdownTrigger.isConnected) return;
          try {
            dropdownTrigger.focus({ preventScroll: true });
          } catch (error) {
            dropdownTrigger.focus();
          }
        });
        return;
      }
      event.preventDefault();
      animateClose(activeSheet);
      return;
    }

    if (event.key !== 'Tab') return;
    var focusable = getFocusableElements(activeSheet);
    if (!focusable.length) {
      event.preventDefault();
      activeSheet.focus();
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var current = document.activeElement;
    if (event.shiftKey && (current === first || !activeSheet.contains(current))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (current === last || !activeSheet.contains(current))) {
      event.preventDefault();
      first.focus();
    }
  });

  // ─── INIT ───
  function init() {
    cancelPendingOpen();
    if (activeSheet && !activeSheet.isConnected) {
      activeSheet = null;
      if (backdrop) backdrop.classList.remove('visible');
      document.body.classList.remove('sheet-scroll-lock');
    }

    var modals = [
      document.getElementById('shopModal1'),
      document.getElementById('shopModal2'),
      document.getElementById('shopModal3')
    ];

    modals.forEach(function (modal) {
      if (!modal) return;
      modal.classList.add('glass-sheet', 'glass-enabled');
      injectDragHandle(modal);
      initGesture(modal);
      initBodySwipe(modal);
    });

    // Build the backdrop while the page is idle so the first tap performs no
    // DOM insertion or forced style flush.
    getBackdrop();

    // Override existing open buttons
    var shopBtns = [
      document.getElementById('shopBtn1'),
      document.getElementById('shopBtn2'),
      document.getElementById('shopBtn3')
    ];

    shopBtns.forEach(function (btn, index) {
      if (!btn || !modals[index]) return;
      btn.setAttribute('aria-expanded', modals[index].classList.contains('open') ? 'true' : 'false');
      if (btn.__beautySheetReady) return;
      btn.__beautySheetReady = true;

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        cancelPendingOpen();
        hydrateSheetAfterFirstPaint(modals[index]);

        if (activeSheet && activeSheet !== modals[index]) {
          pendingOpen = modals[index];
          animateClose(activeSheet);
        } else if (activeSheet === modals[index]) {
          animateClose(modals[index]);
        } else {
          animateOpen(modals[index], false, btn);
        }
      });
    });

    // Override existing close buttons
    bindCloseButtons(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 100);
  }

  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (!target) return;
    var containsBottomBar = (target.matches && (target.matches('.bottom-nav') || target.matches('.shop-modal')))
      || (target.querySelector && (target.querySelector('.bottom-nav') || target.querySelector('.shop-modal')));
    if (containsBottomBar) setTimeout(init, 0);
  });

})();
